import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, Legend
} from 'recharts';
import { 
  ArrowLeft, Brain, TrendingUp, TrendingDown, AlertTriangle, 
  CheckCircle, Clock, DollarSign, Eye, ChevronDown, ChevronUp,
  Lightbulb, Target, Shield, Zap, Star
} from 'lucide-react';
import { formatCurrency } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Color palette
const PREDICTION_COLORS = {
  LIKELY_RENEW: '#22C55E',
  UNCERTAIN: '#F59E0B',
  AT_RISK: '#EF4444'
};

const RISK_COLORS = {
  LOW: '#22C55E',
  MEDIUM: '#F59E0B',
  HIGH: '#EF4444'
};

// Probability gauge component
function ProbabilityGauge({ probability, size = 'md' }) {
  const circumference = 2 * Math.PI * 40;
  const strokeDashoffset = circumference - (probability / 100) * circumference;
  
  const getColor = () => {
    if (probability >= 70) return '#22C55E';
    if (probability >= 50) return '#F59E0B';
    return '#EF4444';
  };
  
  const dimensions = size === 'lg' ? 120 : size === 'md' ? 80 : 60;
  
  return (
    <div className="relative" style={{ width: dimensions, height: dimensions }}>
      <svg className="transform -rotate-90" width={dimensions} height={dimensions}>
        <circle
          cx={dimensions / 2}
          cy={dimensions / 2}
          r={dimensions * 0.35}
          fill="none"
          stroke="#E5E7EB"
          strokeWidth={size === 'lg' ? 10 : 6}
        />
        <circle
          cx={dimensions / 2}
          cy={dimensions / 2}
          r={dimensions * 0.35}
          fill="none"
          stroke={getColor()}
          strokeWidth={size === 'lg' ? 10 : 6}
          strokeDasharray={circumference * (dimensions / 100)}
          strokeDashoffset={strokeDashoffset * (dimensions / 100)}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className={`font-bold ${size === 'lg' ? 'text-2xl' : size === 'md' ? 'text-lg' : 'text-sm'}`}>
          {Math.round(probability)}%
        </span>
      </div>
    </div>
  );
}

// Score factor breakdown component
function ScoreFactorBreakdown({ factors, expanded, onToggle }) {
  if (!factors) return null;
  
  const factorsList = [
    { key: 'competitiveness', label: 'Competitiveness', icon: Target },
    { key: 'customerTier', label: 'Customer Tier', icon: Star },
    { key: 'contractValue', label: 'Contract Value', icon: DollarSign },
    { key: 'regionalPattern', label: 'Regional Pattern', icon: TrendingUp },
    { key: 'timePressure', label: 'Time Pressure', icon: Clock }
  ];
  
  return (
    <div className="mt-2">
      <button 
        onClick={onToggle}
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        {expanded ? 'Hide factors' : 'Show factors'}
      </button>
      
      {expanded && (
        <div className="mt-2 space-y-2 p-3 bg-gray-50 rounded-lg">
          {factorsList.map(({ key, label, icon: Icon }) => {
            const factor = factors[key];
            if (!factor) return null;
            
            return (
              <div key={key} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <Icon className="w-3 h-3 text-gray-400" />
                  <span>{label}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px] px-1">
                    {typeof factor.value === 'number' ? 
                      (key === 'contractValue' ? formatCurrency(factor.value) : 
                       key === 'timePressure' ? `${factor.value}d` : 
                       `${factor.value}%`) : 
                      factor.value}
                  </Badge>
                  <span className="font-medium">+{factor.contribution}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Contract prediction card
function PredictionCard({ prediction, onViewDetails }) {
  const [expanded, setExpanded] = useState(false);
  
  const outcomeConfig = {
    LIKELY_RENEW: { label: 'Likely Renew', icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
    UNCERTAIN: { label: 'Uncertain', icon: AlertTriangle, color: 'text-yellow-600', bg: 'bg-yellow-50' },
    AT_RISK: { label: 'At Risk', icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50' }
  };
  
  const config = outcomeConfig[prediction.prediction.outcome];
  const Icon = config.icon;
  
  return (
    <Card className={`border-l-4 ${
      prediction.prediction.outcome === 'LIKELY_RENEW' ? 'border-l-green-500' :
      prediction.prediction.outcome === 'UNCERTAIN' ? 'border-l-yellow-500' :
      'border-l-red-500'
    }`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-semibold">{prediction.accountName}</h4>
              <Badge variant="outline" className="text-[10px]">
                {prediction.accountTier}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">{prediction.contractNumber}</p>
            
            <div className="flex items-center gap-4 mt-2">
              <div className="flex items-center gap-1 text-sm">
                <DollarSign className="w-4 h-4 text-gray-400" />
                <span className="font-medium">{formatCurrency(prediction.annualValue)}</span>
              </div>
              <div className="flex items-center gap-1 text-sm">
                <Clock className="w-4 h-4 text-gray-400" />
                <span className={prediction.daysToExpiration <= 30 ? 'text-red-600 font-medium' : ''}>
                  {prediction.daysToExpiration} days
                </span>
              </div>
              <Badge variant="secondary" className="text-[10px]">
                {prediction.isoRegion}
              </Badge>
            </div>
          </div>
          
          <div className="flex flex-col items-center">
            <ProbabilityGauge probability={prediction.prediction.probability} size="sm" />
            <Badge className={`mt-1 ${config.bg} ${config.color} text-[10px]`}>
              <Icon className="w-3 h-3 mr-1" />
              {config.label}
            </Badge>
          </div>
        </div>
        
        {/* Recommendations */}
        {prediction.recommendations && prediction.recommendations.length > 0 && (
          <div className="mt-3 pt-3 border-t">
            <div className="text-xs font-medium mb-2 flex items-center gap-1">
              <Lightbulb className="w-3 h-3 text-yellow-500" />
              Recommendations
            </div>
            <div className="space-y-1">
              {prediction.recommendations.map((rec, idx) => (
                <div 
                  key={idx}
                  className={`text-xs p-2 rounded ${
                    rec.priority === 'CRITICAL' ? 'bg-red-50 text-red-700' :
                    rec.priority === 'HIGH' ? 'bg-orange-50 text-orange-700' :
                    'bg-blue-50 text-blue-700'
                  }`}
                >
                  <span className="font-medium">{rec.action}</span>
                  <span className="text-muted-foreground ml-1">• {rec.impact}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <ScoreFactorBreakdown 
          factors={prediction.scoreFactors} 
          expanded={expanded}
          onToggle={() => setExpanded(!expanded)}
        />
        
        <div className="mt-3 flex justify-end">
          <Button 
            variant="outline" 
            size="sm" 
            className="h-7 text-xs"
            onClick={() => onViewDetails(prediction.contractId)}
          >
            <Eye className="w-3 h-3 mr-1" />
            View Contract
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function PredictiveForecasting() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');

  const { data: predictionsData, isLoading, error } = useQuery({
    queryKey: ['renewal-predictions'],
    queryFn: async () => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.get(`${BACKEND_URL}/api/predictions/renewals`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
  });

  const handleViewDetails = (contractId) => {
    navigate(`/contracts/${contractId}`);
  };

  const predictions = predictionsData?.predictions || [];
  const summary = predictionsData?.summary || {};
  const insights = predictionsData?.insights || [];

  // Filter predictions based on tab
  const filteredPredictions = predictions.filter(p => {
    if (activeTab === 'all') return true;
    if (activeTab === 'at-risk') return p.prediction.outcome === 'AT_RISK';
    if (activeTab === 'uncertain') return p.prediction.outcome === 'UNCERTAIN';
    if (activeTab === 'likely') return p.prediction.outcome === 'LIKELY_RENEW';
    return true;
  });

  // Prepare pie chart data
  const pieData = [
    { name: 'Likely Renew', value: summary.likelyRenew?.count || 0, color: PREDICTION_COLORS.LIKELY_RENEW },
    { name: 'Uncertain', value: summary.uncertain?.count || 0, color: PREDICTION_COLORS.UNCERTAIN },
    { name: 'At Risk', value: summary.atRisk?.count || 0, color: PREDICTION_COLORS.AT_RISK }
  ];

  // Prepare value bar chart data
  const valueData = [
    { name: 'Likely Renew', value: summary.likelyRenew?.value || 0 },
    { name: 'Uncertain', value: summary.uncertain?.value || 0 },
    { name: 'At Risk', value: summary.atRisk?.value || 0 }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Brain className="w-12 h-12 mx-auto mb-4 text-[#09409C] animate-pulse" />
          <p className="text-muted-foreground">Analyzing renewal patterns...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="predictive-forecasting">
      {/* Header */}
      <div className="bg-white border-b p-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/executive')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Brain className="w-7 h-7 text-[#09409C]" />
                <span className="text-gradient-primary">Predictive Renewal Forecasting</span>
              </h1>
              <p className="text-sm text-muted-foreground">
                AI-powered predictions to help navigate every renewal
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-5 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-5 h-5 text-blue-600" />
                <span className="text-sm text-blue-700">Total Analyzed</span>
              </div>
              <div className="text-3xl font-bold text-blue-800">{summary.totalContracts || 0}</div>
              <div className="text-xs text-blue-600 mt-1">Contracts expiring in 180 days</div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-sm text-green-700">Likely Renew</span>
              </div>
              <div className="text-3xl font-bold text-green-800">{summary.likelyRenew?.count || 0}</div>
              <div className="text-xs text-green-600 mt-1">{formatCurrency(summary.likelyRenew?.value || 0)}</div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
                <span className="text-sm text-yellow-700">Uncertain</span>
              </div>
              <div className="text-3xl font-bold text-yellow-800">{summary.uncertain?.count || 0}</div>
              <div className="text-xs text-yellow-600 mt-1">{formatCurrency(summary.uncertain?.value || 0)}</div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-5 h-5 text-red-600" />
                <span className="text-sm text-red-700">At Risk</span>
              </div>
              <div className="text-3xl font-bold text-red-800">{summary.atRisk?.count || 0}</div>
              <div className="text-xs text-red-600 mt-1">{formatCurrency(summary.atRisk?.value || 0)}</div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-5 h-5 text-purple-600" />
                <span className="text-sm text-purple-700">Avg Probability</span>
              </div>
              <div className="text-3xl font-bold text-purple-800">{summary.avgProbability || 0}%</div>
              <div className="text-xs text-purple-600 mt-1">Overall renewal likelihood</div>
            </CardContent>
          </Card>
        </div>

        {/* Insights */}
        {insights.length > 0 && (
          <div className="grid grid-cols-2 gap-4 mb-6">
            {insights.map((insight, idx) => (
              <Card 
                key={idx}
                className={insight.type === 'positive' ? 'bg-green-50 border-green-200' : 
                           insight.type === 'warning' ? 'bg-orange-50 border-orange-200' :
                           'bg-blue-50 border-blue-200'}
              >
                <CardContent className="py-3 flex items-center gap-3">
                  {insight.type === 'positive' ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : insight.type === 'warning' ? (
                    <AlertTriangle className="w-5 h-5 text-orange-600" />
                  ) : (
                    <Lightbulb className="w-5 h-5 text-blue-600" />
                  )}
                  <span className="text-sm">{insight.message}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Charts and Predictions */}
        <div className="grid grid-cols-3 gap-6">
          {/* Charts */}
          <div className="space-y-6">
            {/* Distribution Pie Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Prediction Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-4 mt-2">
                  {pieData.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-1 text-xs">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                      <span>{item.name}: {item.value}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Value at Risk */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Value by Prediction</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={valueData} layout="vertical">
                    <XAxis type="number" tickFormatter={(v) => `$${(v/1000000).toFixed(1)}M`} />
                    <YAxis type="category" dataKey="name" width={80} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Bar dataKey="value" fill="#09409C" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* High Value At Risk */}
            {summary.highValueAtRisk && summary.highValueAtRisk.length > 0 && (
              <Card className="border-red-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2 text-red-700">
                    <AlertTriangle className="w-5 h-5" />
                    High-Value At Risk
                  </CardTitle>
                  <CardDescription>Contracts &gt;$500K at risk</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {summary.highValueAtRisk.slice(0, 5).map((contract, idx) => (
                      <div 
                        key={idx}
                        className="flex items-center justify-between p-2 bg-red-50 rounded cursor-pointer hover:bg-red-100"
                        onClick={() => handleViewDetails(contract.contractId)}
                      >
                        <div>
                          <div className="font-medium text-sm">{contract.accountName}</div>
                          <div className="text-xs text-muted-foreground">{contract.daysToExpiration} days</div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-red-700">{formatCurrency(contract.annualValue)}</div>
                          <Badge className="bg-red-100 text-red-800 text-[10px]">
                            {contract.prediction.probability}%
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Predictions List */}
          <div className="col-span-2">
            <Card className="h-full flex flex-col">
              <CardHeader className="flex-shrink-0">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Contract Predictions</CardTitle>
                  <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="h-8">
                      <TabsTrigger value="all" className="text-xs h-7">All ({predictions.length})</TabsTrigger>
                      <TabsTrigger value="at-risk" className="text-xs h-7">At Risk ({summary.atRisk?.count || 0})</TabsTrigger>
                      <TabsTrigger value="uncertain" className="text-xs h-7">Uncertain ({summary.uncertain?.count || 0})</TabsTrigger>
                      <TabsTrigger value="likely" className="text-xs h-7">Likely ({summary.likelyRenew?.count || 0})</TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
              </CardHeader>
              <CardContent className="flex-1 overflow-hidden">
                <ScrollArea className="h-full pr-4" style={{ maxHeight: 'calc(100vh - 380px)' }}>
                  <div className="space-y-4">
                    {filteredPredictions.map((prediction) => (
                      <PredictionCard 
                        key={prediction.contractId}
                        prediction={prediction}
                        onViewDetails={handleViewDetails}
                      />
                    ))}
                    {filteredPredictions.length === 0 && (
                      <div className="text-center py-12 text-muted-foreground">
                        <Brain className="w-12 h-12 mx-auto mb-4 opacity-20" />
                        <p>No contracts in this category</p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
